import { create } from "zustand";
import { type ContrastThresholds, DEFAULT_THRESHOLDS } from "@/color/contrast";
import type { VisionMode } from "@/color/cvd";
import type { PaletteKey } from "@/model/palette";
import { type Resolved, resolveTheme } from "@/model/resolve";
import { type Theme, touch } from "@/model/theme";
import type { TokenId } from "@/model/tokens";
import { DEFAULT_STARTER, starterTheme } from "@/presets/starters";

export type Selection = { kind: "palette"; key: PaletteKey } | { kind: "token"; id: TokenId };

export type PreviewTab =
  | "workspace"
  | "neovim"
  | "terminal"
  | "herdr"
  | "claude"
  | "cursor"
  | "weechat"
  | "contrast";

const HISTORY_LIMIT = 200;
/** Edits with the same key closer together than this collapse into one undo step. */
const COALESCE_MS = 1200;

export interface EditorState {
  draft: Theme;
  resolved: Resolved;
  /** Last saved version of the draft's preset, or null if never saved. */
  saved: Theme | null;
  past: Theme[];
  future: Theme[];
  lastEdit: { key: string; at: number } | null;
  selection: Selection | null;
  /** Theme pinned for A/B comparison and whether it is currently shown. */
  compare: { theme: Theme; label: string } | null;
  showCompare: boolean;
  vision: VisionMode;
  inspect: boolean;
  previewTab: PreviewTab;
  thresholds: ContrastThresholds;
  dogfood: boolean;

  /** Applies an edit; `coalesce` merges rapid edits (slider drags) into one undo step. */
  edit: (fn: (t: Theme) => Theme, coalesce?: string) => void;
  undo: () => void;
  redo: () => void;
  /** Replaces the draft (opening a preset, a share link, an import). Undoable. */
  open: (theme: Theme, saved: Theme | null) => void;
  markSaved: (theme: Theme) => void;
  select: (s: Selection | null) => void;
  setCompare: (c: { theme: Theme; label: string } | null) => void;
  toggleCompare: (show?: boolean) => void;
  set: (
    patch: Partial<
      Pick<EditorState, "vision" | "inspect" | "previewTab" | "thresholds" | "dogfood">
    >,
  ) => void;
}

const initial = starterTheme(DEFAULT_STARTER) as Theme;

export const useEditor = create<EditorState>()((set, get) => ({
  draft: initial,
  resolved: resolveTheme(initial),
  saved: null,
  past: [],
  future: [],
  lastEdit: null,
  selection: { kind: "palette", key: "blue" },
  compare: null,
  showCompare: false,
  vision: "normal",
  inspect: false,
  previewTab: "workspace",
  thresholds: DEFAULT_THRESHOLDS,
  dogfood: false,

  edit: (fn, coalesce) => {
    const { draft, past, lastEdit } = get();
    const next = touch(fn(draft));
    const now = Date.now();
    const merge = coalesce && lastEdit?.key === coalesce && now - lastEdit.at < COALESCE_MS;
    set({
      draft: next,
      resolved: resolveTheme(next),
      past: merge ? past : [...past, draft].slice(-HISTORY_LIMIT),
      future: [],
      lastEdit: coalesce ? { key: coalesce, at: now } : null,
      showCompare: false,
    });
  },

  undo: () => {
    const { past, draft, future } = get();
    const prev = past.at(-1);
    if (!prev) return;
    set({
      draft: prev,
      resolved: resolveTheme(prev),
      past: past.slice(0, -1),
      future: [draft, ...future],
      lastEdit: null,
    });
  },

  redo: () => {
    const { past, draft, future } = get();
    const [next, ...rest] = future;
    if (!next) return;
    set({
      draft: next,
      resolved: resolveTheme(next),
      past: [...past, draft],
      future: rest,
      lastEdit: null,
    });
  },

  open: (theme, saved) => {
    const { draft, past } = get();
    set({
      draft: theme,
      resolved: resolveTheme(theme),
      saved,
      past: [...past, draft].slice(-HISTORY_LIMIT),
      future: [],
      lastEdit: null,
      showCompare: false,
    });
  },

  markSaved: (theme) => set({ saved: theme }),
  select: (selection) => set({ selection }),
  setCompare: (compare) => set({ compare, showCompare: false }),
  toggleCompare: (show) => {
    const { compare, showCompare } = get();
    if (!compare) return;
    set({ showCompare: show ?? !showCompare });
  },
  set: (patch) => set(patch),
}));

/** The theme the previews show: the draft, or B while A/B comparison is toggled on. */
export function useShownTheme(): { theme: Theme; resolved: Resolved; isB: boolean } {
  const draft = useEditor((s) => s.draft);
  const resolved = useEditor((s) => s.resolved);
  const compare = useEditor((s) => s.compare);
  const showCompare = useEditor((s) => s.showCompare);
  const bResolved = useCompareResolved(compare?.theme);
  if (showCompare && compare && bResolved)
    return { theme: compare.theme, resolved: bResolved, isB: true };
  return { theme: draft, resolved, isB: false };
}

let cache: { theme: Theme; resolved: Resolved } | null = null;
function useCompareResolved(theme: Theme | undefined): Resolved | null {
  if (!theme) return null;
  if (cache?.theme !== theme) cache = { theme, resolved: resolveTheme(theme) };
  return cache.resolved;
}

export function isDirty(s: Pick<EditorState, "draft" | "saved">): boolean {
  if (!s.saved) return true;
  const strip = (t: Theme) => JSON.stringify({ ...t, meta: { ...t.meta, updatedAt: "" } });
  return strip(s.saved) !== strip(s.draft);
}
